import { useRef, useEffect, useState, useCallback } from 'react';
import { testimonialsData as staticTestimonials } from '../../data/testimonialData';

const Testimonials = ({ testimonials = [], loading = false }) => {
    const [testimonialsData, setTestimonialsData] = useState(null);
    const [isDataReady, setIsDataReady] = useState(false);

    useEffect(() => {
        if (loading) {
            setIsDataReady(false);
            return;
        }

        try {
            if (testimonials && Array.isArray(testimonials) && testimonials.length > 0) {
                console.log('Using Firestore testimonials:', testimonials);
                setTestimonialsData(testimonials);
            } else {
                console.log('Using static testimonials (no Firestore data)');
                setTestimonialsData(staticTestimonials);
            }
            setIsDataReady(true);
        } catch (err) {
            console.error('Error setting testimonials data:', err);
            setTestimonialsData(staticTestimonials);
            setIsDataReady(true);
        }
    }, [testimonials, loading]);
    const scrollContainerRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isScrolling, setIsScrolling] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const trackRef = useRef(null);

    const [cardWidth, setCardWidth] = useState(window.innerWidth < 768 ? 300 : 350);
    const GAP = 48; // gap-12 = 48px
    const CARD_TOTAL_WIDTH = cardWidth + GAP;

    // Handle resize to update card width
    useEffect(() => {
        const handleResize = () => {
            const newWidth = window.innerWidth < 768 ? 300 : 350;
            if (newWidth !== cardWidth) {
                setCardWidth(newWidth);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [cardWidth]);

    const safeData = testimonialsData || [];
    const extendedData = safeData.length > 0
        ? [...safeData, ...safeData, ...safeData, ...safeData, ...safeData, ...safeData]
        : [];
    const originalLength = safeData.length;

    const getTranslateX = useCallback((index) => {
        // Mobile: Show right card fully (0.92 factor), Desktop: Show centered (0.4 factor)
        const offsetFactor = cardWidth === 300 ? 0.92 : 0.4;
        const offsetFromLeft = -(cardWidth * offsetFactor);
        return offsetFromLeft - (index * CARD_TOTAL_WIDTH);
    }, [cardWidth, CARD_TOTAL_WIDTH]);

    useEffect(() => {
        setCurrentIndex(originalLength);
    }, [originalLength]);

    useEffect(() => {
        if (isScrolling) return;

        if (currentIndex >= originalLength * 2) {
            const newIndex = currentIndex - originalLength;
            setTimeout(() => {
                if (trackRef.current) {
                    trackRef.current.style.transition = 'none';
                    setCurrentIndex(newIndex);
                    // Re-enable transition after browser paint
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (trackRef.current) {
                                trackRef.current.style.transition = 'transform 0.5s ease-in-out';
                            }
                        });
                    });
                }
            }, 0);
        }
        // If we've gone too far left, reset to middle set
        else if (currentIndex < originalLength) {
            const newIndex = currentIndex + originalLength;
            setTimeout(() => {
                if (trackRef.current) {
                    trackRef.current.style.transition = 'none';
                    setCurrentIndex(newIndex);
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            if (trackRef.current) {
                                trackRef.current.style.transition = 'transform 0.5s ease-in-out';
                            }
                        });
                    });
                }
            }, 0);
        }
    }, [currentIndex, originalLength, isScrolling]);

    // Scroll function - moves exactly one card
    const scroll = useCallback((direction) => {
        if (isScrolling) return;

        setIsScrolling(true);

        if (direction === 'left') {
            setCurrentIndex(prev => prev - 1);
        } else {
            setCurrentIndex(prev => prev + 1);
        }

        // Unlock after animation completes
        setTimeout(() => {
            setIsScrolling(false);
        }, 550);
    }, [isScrolling]);

    // Auto scroll timer
    useEffect(() => {
        let interval;
        if (!isHovered) {
            interval = setInterval(() => {
                scroll('right');
            }, 3000);
        }
        return () => clearInterval(interval);
    }, [isHovered, scroll]);

    return (
        <section className="w-full bg-white py-20 overflow-hidden">

            {/* ... [Why Choose Us Section  ... */}
            <div className="max-w-[1440px] mx-auto px-6 md:px-12 mb-24">
                <div className="text-center mb-16">
                    <h2 className="text-4xl md:text-5xl mb-4 font-normal">
                        <span className=" text-black">Why Choose </span>
                        <span className=" text-[#737373]">Alldoer</span>
                    </h2>
                    <p className="text-[#737373] max-w-xl mx-auto text-sm md:text-base">
                        We are a team of 20+ who are passionate about making the world a better place.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-[400px]">
                    <div className="bg-[#F8F5F2] rounded-xl border border-gray-200 p-8 flex flex-col justify-between h-full hover:shadow-lg transition-shadow duration-300">
                        <div>
                            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mb-6 shadow-red-200 shadow-lg">
                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-medium text-gray-900 mb-3">Verified Vendor Network</h3>
                        </div>
                        <p className="text-gray-500 text-sm leading-relaxed">
                            2,000+ our team members around the world who create incredible and amazing projects.
                        </p>
                    </div>

                    <div className="relative rounded-xl border border-gray-200 overflow-hidden group h-full">
                        <img
                            src="./images/parcel.png"
                            alt="Global Sourcing"
                            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                        <div className="relative z-10 p-8 h-full flex flex-col justify-start text-white">
                            <h3 className="xl:text-4xl text-3xl font-bold mb-3">End-to-End <br />Global Sourcing</h3>
                            <p className="text-white text-sm leading-relaxed h-full flex items-end">
                                From idea to doorstep—we manage vendor discovery, production, quality checks, and international shipping under one roof.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-6 h-full">
                        <div className="bg-[#F8F5F2] rounded-xl border border-gray-200 p-8 flex-1 flex flex-col justify-center hover:shadow-lg transition-shadow duration-300">
                            <h3 className="text-xl font-medium text-gray-900 mb-3">Custom-First Approach</h3>
                            <p className="text-gray-500 text-xs md:text-sm leading-relaxed">
                                Not finding what you want? Share your needs and we'll source it for you.
                            </p>
                        </div>
                        <div className="bg-[#F8F5F2] rounded-xl border border-gray-200 p-8 flex-1 flex flex-col justify-center hover:shadow-lg transition-shadow duration-300">
                            <h3 className="text-xl font-medium text-gray-900 mb-3">Quality Control at Source</h3>
                            <p className="text-gray-500 text-xs md:text-sm leading-relaxed">
                                All orders are inspected before dispatch for quality and compliance.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 
                SECTION 2: TESTIMONIALS 
               */}
            <div
                className="w-full"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >

                {/* Header with Navigation */}
                <div className="max-w-[1440px] mx-auto px-6 md:px-12 flex flex-col md:flex-row justify-between items-end mb-12">
                    <h2 className="text-4xl md:text-5xl">
                        <span className="font-medium text-gray-900">What Our </span>
                        <span className="font-normal text-gray-400">Clients Say</span>
                    </h2>

                    <div className="flex gap-4 mt-6 md:mt-0">
                        <button
                            onClick={() => scroll('left')}
                            className="w-12 h-12 rounded-full border border-gray-200 flex items-center justify-center hover:bg-black hover:text-white hover:border-black transition-all duration-300 group"
                        >
                            <svg className="w-5 h-5 text-gray-600 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </button>
                        <button
                            onClick={() => scroll('right')}
                            className="w-12 h-12 rounded-full border border-red-500 text-red-500 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all duration-300"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Carousel Container - Edge to edge with transform-based sliding */}
                <div
                    ref={scrollContainerRef}
                    className="w-full overflow-hidden pb-10"
                >
                    {!isDataReady ? (
                        /* Loading Skeleton */
                        <div className="flex gap-12 items-start" style={{ paddingLeft: '24px' }}>
                            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                                <div
                                    key={`skeleton-${i}`}
                                    className={`
                                        w-[300px] md:w-[350px] flex-shrink-0 
                                        bg-[#F8F5F2] rounded-2xl p-8 flex flex-col justify-between 
                                        animate-pulse
                                        ${i % 2 === 0 ? 'h-[360px]' : 'h-[340px]'} 
                                    `}
                                >
                                    <div className="space-y-3">
                                        <div className="h-4 bg-gray-300 rounded w-full"></div>
                                        <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                                        <div className="h-4 bg-gray-300 rounded w-4/6"></div>
                                    </div>
                                    <div className="flex items-center gap-4 mt-6">
                                        <div className="w-12 h-12 rounded-full bg-gray-300"></div>
                                        <div className="space-y-2">
                                            <div className="h-3 bg-gray-300 rounded w-24"></div>
                                            <div className="h-2 bg-gray-300 rounded w-16"></div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div
                            ref={trackRef}
                            className="flex gap-12 items-start"
                            style={{
                                transform: `translateX(${getTranslateX(currentIndex)}px)`,
                                transition: 'transform 0.5s ease-in-out'
                            }}
                        >
                            {extendedData.map((item, index) => {
                                const originalIndex = index % safeData.length;
                                const isTall = originalIndex % 2 === 0;

                                return (
                                    <div
                                        key={`${item.id}-${index}`}
                                        data-card="true"
                                        className={`
                                        w-[300px] md:w-[350px] border border-gray-200 flex-shrink-0 
                                        bg-[#F8F5F2] rounded-xl p-8 flex flex-col justify-between 
                                        hover:shadow-xl transition-shadow duration-300 hover:border-gray-200
                                        ${isTall ? 'lg:h-[430px] h-[394px] ' : 'lg:h-[394px] h-[340px]'} 
                                    `}
                                    >
                                        <div>
                                            <p className="text-lg text-gray-800 leading-relaxed font-normal">
                                                "{item.feedback}"
                                            </p>
                                        </div>

                                        <div className="flex items-center gap-4 mt-6">
                                            {item.image ? (
                                                <img
                                                    src={item.image}
                                                    alt={item.name}
                                                    className="w-12 h-12 rounded-full object-cover ring-2 ring-white"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gray-300 text-gray-600 font-semibold ring-2 ring-white">
                                                    {item.name?.charAt(0) || '?'}
                                                </div>
                                            )}
                                            <div>
                                                <h4 className="text-sm font-bold text-gray-900">{item.name}</h4>
                                                <p className="text-xs text-gray-500 uppercase tracking-wide mt-0.5">{item.organization && item.location ? `${item.organization}, ${item.location}` : item.organization || item.location || ""}</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default Testimonials;